import * as SwitchPrimitive from "@radix-ui/react-switch";
import { cva } from "class-variance-authority";
import * as React from "react";

import { FontAwesomeIcon, type IconDefinition } from "@performyard/fontawesome/react-fontawesome";

import { cn } from "../../../lib/utils";

import { useInputField } from "../../molecules/InputWrapper/InputContext";
import { OffSVG } from "./internal/OffTextSVG";
import { OnSVG } from "./internal/OnTextSVG";

const toggleVariants = cva(
	"relative shrink-0 cursor-pointer items-center rounded-full border p-0 outline-hidden transition-all focus-visible:ring-3 disabled:cursor-not-allowed disabled:opacity-disabled",
	{
		variants: {
			color: {
				primary: "bg-primary-5",
				neutral: "bg-neutral-3",
			},
			size: {
				16: "h-4 w-8",
				20: "h-5 w-10",
				24: "h-6 w-12",
			},
			errored: {
				true: "border-red-6",
				false: "border-transparent",
			},
		},
		defaultVariants: {
			size: 16,
			errored: false,
		},
	}
);

const toggleThumbVariants = cva(
	"pointer-events-none block size-full rounded-full bg-neutral-0 ring-0 transition-transform data-[state=checked]:translate-x-[calc(100%_+_4px)] data-[state=unchecked]:translate-x-0.5",
	{
		variants: {
			size: {
				16: "size-3",
				20: "size-4",
				24: "size-5",
			},
		},
		defaultVariants: {
			size: 16,
		},
	}
);

const toggleIconOnVariants = cva("", {
	variants: {
		size: {
			16: "size-2 pl-1",
			20: "size-3 pl-1",
			24: "size-3 pl-1.5",
		},
	},
});

const toggleIconOffVariants = cva("", {
	variants: {
		size: {
			16: "size-2 pr-1",
			20: "size-3 pr-1.5",
			24: "size-3 pr-1.5",
		},
	},
});

const toggleTextOnVariants = cva("pt-0.5", {
	variants: {
		size: {
			16: "pl-0.5",
			20: "pl-1",
			24: "pl-1",
		},
	},
});

const toggleTextOffVariants = cva("pt-0.5", {
	variants: {
		size: {
			16: "pr-0.5",
			20: "pr-1",
			24: "pr-1",
		},
	},
});

type ToggleProps = {
	/**
	 * The ref object to the toggle button
	 */
	ref?: React.RefObject<HTMLButtonElement>;
	/**
	 * The checked state of the toggle
	 */
	checked?: boolean;
	/**
	 * The callback function to be called when the checked state changes
	 */
	onCheckedChange?: (checked: boolean) => void;
	/**
	 * Whether the toggle is disabled
	 */
	disabled?: boolean;
	/**
	 * The color of the toggle
	 */
	color?: "primary" | "neutral";
	/**
	 * The size of the toggle
	 */
	size?: 16 | 20 | 24;
	/**
	 * The id of the toggle
	 */
	id?: string;
	/**
	 * The test id of the toggle
	 */
	"data-testid"?: string;
	/**
	 * The aria label of the toggle
	 */
	"aria-label"?: string;
	/**
	 * The aria labelledby of the toggle
	 */
	"aria-labelledby"?: string;
	/**
	 * The icon to display when the toggle is checked
	 */
	iconTrue?: IconDefinition;
	/**
	 * The icon to display when the toggle is unchecked
	 */
	iconFalse?: IconDefinition;
	/**
	 * Whether to show the text of the toggle, only shows on/off text if true
	 */
	showText?: boolean;
	/**
	 * The className to apply to the toggle
	 */
	dangerouslySetClassName?: string;
};

function Toggle({
	ref,
	dangerouslySetClassName,
	color = "primary",
	checked,
	onCheckedChange,
	disabled,
	size = 16,
	id,
	"data-testid": dataTestId,
	"aria-label": ariaLabel,
	"aria-labelledby": ariaLabelledby,
	iconTrue,
	iconFalse,
	showText = false,
}: ToggleProps) {
	const { id: contextId, disabled: contextDisabled } = useInputField();
	const generatedId = React.useId();
	const inputId = id || contextId || generatedId;

	const isDisabled = disabled || contextDisabled || false;

	return (
		<SwitchPrimitive.Root
			ref={ref}
			id={inputId}
			data-slot="switch"
			className={cn(toggleVariants({ color, size }), dangerouslySetClassName)}
			checked={checked}
			onCheckedChange={onCheckedChange}
			disabled={isDisabled}
			aria-label={ariaLabel}
			aria-labelledby={ariaLabelledby}
			data-testid={dataTestId}
		>
			<div className="absolute top-0 left-0 inline-flex size-full items-center justify-between text-neutral-0">
				{showText ? (
					<>
						<OnSVG className={toggleTextOnVariants({ size })} size={size} fontSize={18} />
						<OffSVG className={toggleTextOffVariants({ size })} size={size} fontSize={17} />
					</>
				) : (
					<>
						{iconTrue && <FontAwesomeIcon icon={iconTrue} className={toggleIconOnVariants({ size })} />}
						{iconFalse && <FontAwesomeIcon icon={iconFalse} className={toggleIconOffVariants({ size })} />}
					</>
				)}
			</div>
			<SwitchPrimitive.Thumb data-slot="toggle-thumb" className={cn(toggleThumbVariants({ size }))} />
		</SwitchPrimitive.Root>
	);
}

Toggle.displayName = "Toggle";

export { Toggle, type ToggleProps };
